import React, { useState, useEffect } from 'react';
import SparklesIcon from './icons/SparklesIcon';
import * as api from '../services/apiService';
import { useUI } from '../contexts/UIProvider';
import { formatTimestamp } from '../utils/formatTimestamp';

const FeedbackPage: React.FC = () => {
    const [text, setText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [history, setHistory] = useState<{ id: string; text: string; timestamp: string | null }[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const { showToast } = useUI();

    useEffect(() => {
        api.getMyFeedback()
            .then(res => setHistory(res.items))
            .catch(() => {})
            .finally(() => setLoadingHistory(false));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim()) return;

        setIsSubmitting(true);
        try {
            await api.postFeedback(text);
            showToast("Feedback received! Thank you for helping us grow.");
            const newItem = { id: Date.now().toString(), text: text.trim(), timestamp: new Date().toISOString() };
            setHistory(prev => [newItem, ...prev]);
            setText('');
        } catch {
            showToast("Failed to send feedback. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 font-urbanist animate-in fade-in duration-500">
            {/* Submit form */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-brand-orange/10 p-3 rounded-xl">
                        <SparklesIcon className="h-7 w-7 text-brand-orange" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-extrabold text-white tracking-tight">Suggest Improvements</h2>
                        <p className="text-brand-muted text-sm">We'd love to hear your ideas for making beYourAMP even better.</p>
                    </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <textarea
                        rows={5}
                        value={text}
                        onChange={e => setText(e.target.value)}
                        className="w-full bg-gray-800 text-white rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none font-medium border border-gray-700 placeholder-gray-600 text-sm"
                        placeholder="What's on your mind? Features, bugs, UX ideas..."
                    />
                    <div className="flex justify-between items-center">
                        <span className={`text-xs font-mono ${text.length > 1000 ? 'text-red-400' : 'text-brand-muted'}`}>
                            {text.length} / 1000
                        </span>
                        <button
                            type="submit"
                            disabled={isSubmitting || !text.trim() || text.length > 1000}
                            className="bg-brand-orange text-white font-bold px-8 py-3 rounded-full hover:bg-orange-600 disabled:opacity-50 transition-all shadow-lg shadow-brand-orange/20 uppercase tracking-widest text-xs"
                        >
                            {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                        </button>
                    </div>
                </form>
            </div>

            {/* History */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                <h3 className="text-[10px] font-black text-brand-orange uppercase tracking-[0.2em] mb-4">
                    Your Submitted Feedback
                </h3>
                {loadingHistory ? (
                    <p className="text-brand-muted text-sm animate-pulse py-4">Loading history...</p>
                ) : history.length === 0 ? (
                    <div className="text-center py-10 border-2 border-dashed border-gray-800 rounded-xl">
                        <p className="text-brand-muted text-sm italic">No feedback submitted yet.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {history.map(item => (
                            <div key={item.id} className="bg-gray-800/40 p-4 rounded-xl border border-gray-800">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[10px] font-black text-brand-muted uppercase tracking-widest">Sent</span>
                                    <span className="text-[10px] text-brand-muted font-mono">{item.timestamp ? formatTimestamp(item.timestamp) : '—'}</span>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbackPage;
